<template>
  <div
    class="app-downloading-page">
    App
    <div class="app-name">
      {{ appName }}
    </div>
    <div class="toolbar">
      <div
        v-for="item in toolbarItems"
        :key="item.label"
        class="toolbar-item"
        @click="handleClick(item.label)">
        <div class="toolbar-item-label">
          {{ item.label }}
          <el-tooltip
            v-if="item.tooltip"
            class="item"
            placement="top">
            <template #content>
              <div style="max-width: calc(100vw - 40px)">
                {{ item.tooltip }}
              </div>
            </template>
            <el-icon>
              <QuestionFilled/>
            </el-icon>
          </el-tooltip>
        </div>
        <div v-if="item.href === undefined" class="loading-dummy">
          <el-icon class="is-loading">
            <Loading/>
          </el-icon>
          Searching files...
        </div>
        <div v-else-if="item.href === null" class="empty-dummy result-null">
          <i class="fa fa-hourglass-o"/>
          The files are being generated. You can download them later. Please, wait or click "Retry".
        </div>
        <div v-else-if="item.href === 'error'" class="empty-dummy">
          <el-icon>
            <Close/>
          </el-icon>
          {{ getEndingFiles(item.label) }} not available
        </div>
        <a v-else-if="item.href" :href="item.href" :class="{disabled: disabledButtons.includes(item.label)}">
          <i :class="item.icon"/>
          Download
        </a>
      </div>
    </div>
    <div v-if="links.other === null || links.public === null" class="retry-state">
      <div class="retry-btn" @click="getLinks">
        Retry
      </div>
      or wait {{ remainingTimeToRetry }} sec
    </div>
  </div>
</template>

<script>
import '@/assets/sass/style/downloadPage.scss'
import { mapActions, mapGetters } from 'vuex'
import { ElTooltip } from 'element-plus'
import { capitalizeFirstChar, getInternalLinksForApp, getPublicLinkForApp } from '@/utils/helpers'

export default {
  name: 'AppDownloadingPage',
  components: {
    ElTooltip
  },
  data: () => ({
    appId: undefined,
    appName: undefined,
    flags: undefined,
    linkType: undefined,
    links: {
      other: undefined,
      source: undefined,
      public: undefined,
      info: undefined
    },
    retryDelay: 15,
    retryTimerId: undefined,
    remainingTimeToRetry: undefined,
    disabledButtons: [],
    initTypes: [
      {
        label: 'ASO',
        tooltip: 'ASO info and images for all localizations'
      },
      {
        label: 'AAB file',
        tooltip: 'AAB file'
      },
      {
        label: 'keys',
        tooltip: 'keystore file and passwords'
      },
      {
        label: 'UX/UI',
        tooltip: 'app UX/UI prototype file'
      },
      {},
      {
        label: 'Text ASO',
        tooltip: 'Text ASO info for all localizations'
      },
      {
        label: 'Graphics ASO',
        tooltip: 'Graphics ASO images for all localizations'
      },
      {
        label: 'Icon ASO',
        tooltip: 'Icon ASO images for all localizations'
      },
    ]
  }),
  computed: {
    ...mapGetters(['selectedApplication']),
    selectedTypes() {
      if (this.linkType !== 'public' || !this.flags) return
      const binaryStr = Number(this.flags).toString(2)
      const result = []
      for (let i = binaryStr.length - 3; i >= 0; i--) {
        if (binaryStr[i] === '1') result.push(this.initTypes[binaryStr.length - 3 - i])
      }
      return result
    },
    publicLabel() {
      if (!this.selectedTypes) return
      const labels = this.selectedTypes.map(type => type.label)
      const titleLowCase = labels.length === 1
        ? labels[0]
        : labels.slice(0, -1).join(', ') + ', and ' + labels[labels.length - 1]
      return capitalizeFirstChar(titleLowCase)
    },
    publicTooltip() {
      if (!this.selectedTypes) return
      const tooltips = this.selectedTypes.map(type => type.tooltip)
      const tooltipLowCase = tooltips.length === 1
        ? tooltips[0]
        : tooltips.slice(0, -1).join(', ') + ', and ' + tooltips[tooltips.length - 1]
      return 'Includes ' + tooltipLowCase
    },
    toolbarItems() {
      return [
        {
          label: 'Source code',
          icon: 'Document',
          href: this.links.source,
          show: this.linkType === 'internal' || this.$route.query.s === '1'
        },
        {
          label: 'ASO and other files',
          icon: 'Picture',
          tooltip: 'Includes ASO info and images for all localizations, AAB file, keystore file, app info text file',
          href: this.links.other,
          show: this.linkType === 'internal'
        },
        {
          label: 'Information.txt',
          icon: 'Document',
          href: this.links.info,
          show: this.linkType === 'internal'
        },
        {
          label: this.publicLabel,
          icon: 'Picture',
          tooltip: this.publicTooltip,
          href: this.links.public,
          show: this.linkType === 'public' && this.flags
        }
      ].filter(item => item.show)
    }
  },
  watch: {
    remainingTimeToRetry(value, oldValue) {
      if (this.retryTimerId) clearTimeout(this.retryTimerId)
      if (oldValue === 1) {
        this.getLinks()
        return
      }
      this.retryTimerId = setTimeout(() => {
        if (this.remainingTimeToRetry) this.remainingTimeToRetry--
      }, 1000)
    }
  },
  methods: {
    ...mapActions(['downloadApplication']),
    async getLinks() {
      this.remainingTimeToRetry = undefined
      this.retryTimerId = undefined
      if (this.linkType === 'internal') {
        if (this.links.other === null) this.links.other = undefined
        const oldLink = !this.$route.query.id
        const id = !oldLink ? this.$route.query.id : this.appId
        const newLinks = await getInternalLinksForApp(id, oldLink)
        this.links = {
          ...this.links,
          ...newLinks
        }
        if (this.links.other === null) {
          this.remainingTimeToRetry = this.retryDelay
        }
      } else {
        if (this.links.public === null) this.links.public = undefined
        const newLinks = await getPublicLinkForApp(this.$route.query)
        this.links = {
          ...this.links,
          ...newLinks
        }
        if (this.links.public === null) {
          this.remainingTimeToRetry = this.retryDelay
        }
      }
    },
    handleClick(label) {
      if (this.linkType !== 'public' || this.disabledButtons.includes(label)) return
      this.disabledButtons.push(label)
    },
    getEndingFiles(titleField) {
      return titleField === 'Information.txt' ? 'File' : 'Files'
    }
  },
  beforeMount() {
    this.linkType = {
      'App files downloading': 'internal',
      'App files public downloading': 'public'
    }[this.$route.name]
    this.appId = this.$route.query.app
    this.appName = this.$route.query.n
    this.flags = this.$route.query.f
    this.getLinks()
  }
}
</script>

<style scoped>

</style>
